import { useEffect, useState } from "react";
import { BarChart2 } from "lucide-react";
import { apiFetch } from "../api/client";
import { useWorkspace } from "../contexts/WorkspaceContext";

type PersonalAnalytics = {
  total_seconds: number;
  focus_sessions_count: number;
  top_project: { project_id: string; name: string; seconds: number } | null;
  by_day: Array<{ date: string; seconds: number }>;
  by_project: Array<{ project_id: string; name: string; seconds: number }>;
};

type WorkspaceAnalytics = {
  total_seconds: number;
  active_members: number;
  by_member: Array<{ user_id: string; email: string; seconds: number }>;
  by_project: Array<{ project_id: string; name: string; seconds: number }>;
};

type Period = "today" | "week" | "month" | "custom";

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export function Analytics() {
  const { activeWorkspace } = useWorkspace();
  const [period, setPeriod] = useState<Period>("week");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [customError, setCustomError] = useState("");
  const [personal, setPersonal] = useState<PersonalAnalytics | null>(null);
  const [workspace, setWorkspace] = useState<WorkspaceAnalytics | null>(null);
  const [loading, setLoading] = useState(true);

  const isAdmin = activeWorkspace?.role === "admin";

  useEffect(() => {
    const isCustomReady = period !== "custom" || (customFrom !== "" && customTo !== "");
    if (!isCustomReady) return;

    if (period === "custom" && customFrom > customTo) {
      setCustomError("Start date must be before end date");
      return;
    }
    setCustomError("");

    let cancelled = false;
    async function load() {
      setLoading(true);
      let url = `/analytics/me?period=${period}`;
      if (period === "custom") url += `&from=${customFrom}&to=${customTo}`;
      const res = await apiFetch(url);
      if (cancelled) return;
      if (res.ok) setPersonal(await res.json());
      else setPersonal(null);

      if (activeWorkspace && isAdmin) {
        let wsUrl = `/analytics/workspaces/${activeWorkspace.id}?period=${period}`;
        if (period === "custom") wsUrl += `&from=${customFrom}&to=${customTo}`;
        const wsRes = await apiFetch(wsUrl);
        if (!cancelled) {
          if (wsRes.ok) setWorkspace(await wsRes.json());
          else setWorkspace(null);
        }
      } else {
        setWorkspace(null);
      }
      if (!cancelled) setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [period, customFrom, customTo, activeWorkspace]);

  const maxDaySeconds =
    personal && personal.by_day.length > 0
      ? Math.max(...personal.by_day.map((d) => d.seconds), 1)
      : 1;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-ink flex items-center gap-2">
          <BarChart2 size={22} className="text-brand" />
          Analytics
        </h1>
        <div className="flex items-center space-x-1">
          {(["today", "week", "month", "custom"] as Period[]).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`text-sm px-3 py-1.5 rounded-lg transition-colors ${
                period === p
                  ? "bg-brand text-white"
                  : "text-ink-3 hover:text-ink bg-field"
              }`}
            >
              {p.charAt(0).toUpperCase() + p.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {period === "custom" && (
        <div className="space-y-2">
          <div className="flex items-center space-x-3">
            <input
              type="date"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="bg-field border border-white/[0.08] rounded-lg px-3 py-1.5 text-sm text-ink focus:outline-none focus:border-brand transition-colors"
            />
            <span className="text-ink-2 text-sm">to</span>
            <input
              type="date"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              className="bg-field border border-white/[0.08] rounded-lg px-3 py-1.5 text-sm text-ink focus:outline-none focus:border-brand transition-colors"
            />
          </div>
          {customError && <p className="text-red-400 text-sm">{customError}</p>}
        </div>
      )}

      {loading ? (
        <div className="text-ink-2 text-sm">Loading…</div>
      ) : !personal ? (
        <p className="text-ink-3 text-sm">No data available.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-card rounded-xl p-6 border border-white/[0.05]">
              <p className="text-ink-2 text-sm">Total time</p>
              <p className="text-3xl font-bold text-ink mt-1">
                {formatDuration(personal.total_seconds)}
              </p>
            </div>
            <div className="bg-card rounded-xl p-6 border border-white/[0.05]">
              <p className="text-ink-2 text-sm">Focus sessions</p>
              <p className="text-3xl font-bold text-ink mt-1">
                {personal.focus_sessions_count}
              </p>
            </div>
          </div>

          {personal.by_day.length > 0 && (
            <div className="bg-card rounded-xl p-6 border border-white/[0.05]">
              <h2 className="text-lg font-semibold text-ink mb-4">Daily breakdown</h2>
              <div className="flex items-end space-x-1 h-32">
                {personal.by_day.map((day) => (
                  <div key={day.date} className="flex-1 flex flex-col items-center space-y-1">
                    <div
                      className="w-full bg-brand rounded-t min-h-0"
                      style={{
                        height: `${(day.seconds / maxDaySeconds) * 100}%`,
                        minHeight: day.seconds > 0 ? "4px" : "0",
                      }}
                      title={`${day.date}: ${formatDuration(day.seconds)}`}
                    />
                    <span className="text-ink-3 text-xs">{day.date.slice(5)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {personal.by_project.length > 0 && (
            <div className="bg-card rounded-xl p-6 border border-white/[0.05]">
              <h2 className="text-lg font-semibold text-ink mb-4">By project</h2>
              <div className="space-y-3">
                {personal.by_project.map((proj) => {
                  const pct =
                    personal.total_seconds > 0
                      ? Math.round((proj.seconds / personal.total_seconds) * 100)
                      : 0;
                  return (
                    <div key={proj.project_id}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-ink-2 text-sm">{proj.name}</span>
                        <span className="text-ink-2 text-sm">{formatDuration(proj.seconds)}</span>
                      </div>
                      <div className="h-1.5 bg-field rounded-full">
                        <div
                          className="h-1.5 bg-brand rounded-full"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {workspace && (
            <div className="bg-card rounded-xl p-6 space-y-4 border border-white/[0.05]">
              <h2 className="text-lg font-semibold text-ink">
                Team overview — {activeWorkspace?.name}
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-ink-2 text-sm">Total tracked</p>
                  <p className="text-2xl font-bold text-ink mt-1">
                    {formatDuration(workspace.total_seconds)}
                  </p>
                </div>
                <div>
                  <p className="text-ink-2 text-sm">Active members</p>
                  <p className="text-2xl font-bold text-ink mt-1">
                    {workspace.active_members}
                  </p>
                </div>
              </div>
              <div>
                <h3 className="text-sm text-ink-2 mb-2">By member</h3>
                <div className="space-y-2">
                  {workspace.by_member.map((m) => (
                    <div key={m.user_id} className="flex items-center justify-between">
                      <span className="text-ink-2 text-sm">{m.email}</span>
                      <span className="text-ink-2 text-sm">{formatDuration(m.seconds)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
